/* eslint-disable react/prop-types */
/* eslint-disable react/function-component-definition */

import React, { useEffect, useRef, useState } from "react";
import { Box, CircularProgress, Grid, Icon } from "@mui/material";
import BasicModal from "examples/modal/BasicModal/BasicModal";
import FTextField from "components/Form/FTextField";
import ConfigDropdown from "components/Dropdown/ConfigDropdown";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import pxToRem from "assets/theme/functions/pxToRem";
import moment from "moment";
import { useDispatch } from "react-redux";
import uploadImageThunk from "redux/Thunks/ImageUpload";
import { openSnackbar } from "redux/Slice/Notification";
import FullScreenImageComponent from "components/ViewFullImage/ViewImage";
import Constants, { Icons } from "utils/Constants";
import Pdf from "assets/images/pdf.svg";

import {
  INSURANCE_PRODUCTS,
  INSURANCE_CODES,
  POLICY_STATUSES,
  MONTH_OPTIONS,
} from "./AddInsuranceModal";

const PRODUCT_MENU = INSURANCE_PRODUCTS.map((prod) => ({
  [Constants.MONGOOSE_ID]: prod,
  title: prod,
}));

const CODE_MENU = INSURANCE_CODES.map((code) => ({
  [Constants.MONGOOSE_ID]: code,
  title: code,
}));

const MONTH_MENU = MONTH_OPTIONS.map((m) => ({
  [Constants.MONGOOSE_ID]: m,
  title: m,
}));

const STATUS_MENU = POLICY_STATUSES.map((st) => ({
  [Constants.MONGOOSE_ID]: st,
  title: st,
}));

const initialForm = {
  customerName: "",
  mobileNumber: "",
  email: "",
  reference: "",
  imd: "",
  product: "Two Wheeler",
  insuranceCompany: "",
  planName: "",
  policyNumber: "",
  code: "Insurance Dekho",
  policyStatus: "Active",
  inquiryDate: "",
  policyIssueDate: "",
  renewalDate: "",
  month: "",
  grossPremium: "",
  netPremium: "",
  attachments: [],
};

const formatDateForInput = (dateVal) => {
  if (!dateVal) return "";
  return moment(dateVal).format("YYYY-MM-DD");
};

export default function EditInsuranceModal({
  open,
  handleClose,
  handleSubmit,
  loading = false,
  insurance = null,
}) {
  const dispatch = useDispatch();
  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [uploading, setUploading] = useState(false);
  const [fullScreenImage, setFullScreenImage] = useState(null);
  const [fullScreenImageIndex, setFullScreenImageIndex] = useState(0);

  useEffect(() => {
    if (open && insurance) {
      setFormData({
        customerName: insurance.customerName || "",
        mobileNumber: insurance.mobileNumber || "",
        email: insurance.email || "",
        reference: insurance.reference || "",
        imd: insurance.imd || "",
        product: insurance.product || "Two Wheeler",
        insuranceCompany: insurance.insuranceCompany || "",
        planName: insurance.planName || "",
        policyNumber: insurance.policyNumber || "",
        code: insurance.code || "",
        policyStatus: insurance.policyStatus || "Active",
        inquiryDate: formatDateForInput(insurance.inquiryDate),
        policyIssueDate: formatDateForInput(insurance.policyIssueDate),
        renewalDate: formatDateForInput(insurance.renewalDate),
        month: insurance.month || "",
        grossPremium:
          insurance.grossPremium !== undefined && insurance.grossPremium !== null
            ? String(insurance.grossPremium)
            : "",
        netPremium:
          insurance.netPremium !== undefined && insurance.netPremium !== null
            ? String(insurance.netPremium)
            : "",
        attachments: Array.isArray(insurance.attachments) ? [...insurance.attachments] : [],
      });
      setErrors({});
      setUploading(false);
      setFullScreenImage(null);
    }
  }, [open, insurance]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    let finalValue = value;

    if (name === "mobileNumber") {
      finalValue = value.replace(/\D/g, "").slice(0, 10);
    } else if (name === "grossPremium" || name === "netPremium") {
      finalValue = value.replace(/[^\d.]/g, "");
    }

    setFormData((prev) => {
      const updated = { ...prev, [name]: finalValue };

      // Auto-compute month if policyIssueDate is selected and month is empty
      if (name === "policyIssueDate" && finalValue && !prev.month) {
        updated.month = moment(finalValue).format("MMMM");
      }

      return updated;
    });

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleDropdownChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    const allowedFormats = ["image/jpeg", "image/jpg", "image/png", "application/pdf"];

    const validFiles = [];
    for (let i = 0; i < files.length; i += 1) {
      const file = files[i];
      if (allowedFormats.includes(file.type)) {
        validFiles.push(file);
      } else {
        dispatch(
          openSnackbar({
            message: `File "${file.name}" rejected: Only JPG, PNG, and PDF files are allowed.`,
            notificationType: "error",
          })
        );
      }
    }

    if (validFiles.length === 0) {
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }

    setUploading(true);
    try {
      const uploadPromises = validFiles.map((file) =>
        dispatch(uploadImageThunk({ file, type: "Insurance" }))
      );
      const responses = await Promise.all(uploadPromises);

      const newUploaded = responses
        .map((res) => {
          const dataObj = res.payload?.data?.data || res.payload?.data || res.payload;
          const fileName = dataObj?.fileName || dataObj?.name || "";
          const iconUrl = dataObj?.iconUrl || dataObj?.url || "";
          if (iconUrl) {
            return { name: fileName || iconUrl, url: iconUrl };
          }
          return null;
        })
        .filter(Boolean);

      if (newUploaded.length > 0) {
        setFormData((prev) => ({
          ...prev,
          attachments: [...prev.attachments, ...newUploaded],
        }));
        dispatch(
          openSnackbar({
            message: `${newUploaded.length} attachment(s) uploaded successfully`,
            notificationType: "success",
          })
        );
      }
    } catch (err) {
      dispatch(
        openSnackbar({
          message: "Failed to upload attachments. Please try again.",
          notificationType: "error",
        })
      );
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleRemoveAttachment = (indexToRemove) => {
    setFormData((prev) => ({
      ...prev,
      attachments: prev.attachments.filter((_, idx) => idx !== indexToRemove),
    }));
  };

  const handleImageFullView = (url, index) => {
    const isPdf =
      typeof url === "string" &&
      (url.toLowerCase().includes(".pdf") ||
        formData.attachments[index]?.name?.toLowerCase().includes(".pdf"));
    if (isPdf) {
      window.open(url, "_blank");
      return;
    }
    setFullScreenImageIndex(index);
    setFullScreenImage(url);
  };

  const handleNextImage = () => {
    const images = formData.attachments || [];
    if (images.length === 0) return;
    const nextIdx = (fullScreenImageIndex + 1) % images.length;
    setFullScreenImageIndex(nextIdx);
    setFullScreenImage(images[nextIdx]?.url);
  };

  const handlePreviousImage = () => {
    const images = formData.attachments || [];
    if (images.length === 0) return;
    const prevIdx = (fullScreenImageIndex - 1 + images.length) % images.length;
    setFullScreenImageIndex(prevIdx);
    setFullScreenImage(images[prevIdx]?.url);
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.customerName || formData.customerName.trim() === "") {
      newErrors.customerName = "Customer name is required";
    }

    if (!formData.mobileNumber || formData.mobileNumber.trim() === "") {
      newErrors.mobileNumber = "Mobile number is required";
    } else if (!/^\d{10}$/.test(formData.mobileNumber.trim())) {
      newErrors.mobileNumber = "Enter a valid 10-digit mobile number";
    }

    if (!formData.product || formData.product.trim() === "") {
      newErrors.product = "Product selection is required";
    }

    if (formData.email && formData.email.trim() !== "") {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email.trim())) {
        newErrors.email = "Enter a valid email address";
      }
    }

    if (formData.grossPremium && Number(formData.grossPremium) < 0) {
      newErrors.grossPremium = "Gross premium must be positive";
    }

    if (formData.netPremium && Number(formData.netPremium) < 0) {
      newErrors.netPremium = "Net premium must be positive";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const onSubmit = () => {
    if (validate()) {
      const payload = {
        customerName: formData.customerName.trim(),
        mobileNumber: formData.mobileNumber.trim(),
        email: formData.email.trim(),
        reference: formData.reference.trim(),
        imd: formData.imd.trim(),
        product: formData.product.trim(),
        insuranceCompany: formData.insuranceCompany.trim(),
        planName: formData.planName.trim(),
        policyNumber: formData.policyNumber.trim(),
        code: formData.code.trim(),
        policyStatus: formData.policyStatus || "Active",
        inquiryDate: formData.inquiryDate || null,
        policyIssueDate: formData.policyIssueDate || null,
        renewalDate: formData.renewalDate || null,
        month: formData.month ? formData.month.trim() : "",
        grossPremium: formData.grossPremium ? Number(formData.grossPremium) : 0,
        netPremium: formData.netPremium ? Number(formData.netPremium) : 0,
        attachments: formData.attachments || [],
      };

      handleSubmit(payload);
    }
  };

  return (
    <>
      <BasicModal
        open={open}
        handleClose={handleClose}
        title="Edit Insurance Policy"
        actionButton={loading ? "Updating..." : "Update Policy"}
        handleAction={onSubmit}
        minWidth={780}
      >
        <Grid container spacing={2}>
          {/* Customer Information */}
          <Grid item xs={12}>
            <MDTypography
              variant="subtitle2"
              sx={{
                fontSize: pxToRem(14),
                fontWeight: 600,
                color: "#344054",
                borderBottom: "1px solid #EAECF0",
                pb: 0.5,
                mt: 0.5,
              }}
            >
              Customer Details
            </MDTypography>
          </Grid>

          <Grid item xs={12} sm={6}>
            <FTextField
              label="Name of Customer*"
              placeholder="Enter full customer name"
              id="customerName"
              name="customerName"
              value={formData.customerName}
              handleChange={handleChange}
              error={Boolean(errors.customerName)}
              helperText={errors.customerName}
              width="100%"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <FTextField
              label="Mobile Number*"
              placeholder="Enter 10-digit mobile number"
              id="mobileNumber"
              name="mobileNumber"
              value={formData.mobileNumber}
              handleChange={handleChange}
              error={Boolean(errors.mobileNumber)}
              helperText={errors.mobileNumber}
              width="100%"
              inputProps={{ maxLength: 10 }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <FTextField
              label="Email Id"
              placeholder="Enter customer email address"
              id="email"
              name="email"
              type="email"
              value={formData.email}
              handleChange={handleChange}
              error={Boolean(errors.email)}
              helperText={errors.email}
              width="100%"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <FTextField
              label="Reference"
              placeholder="Enter reference name or details"
              id="reference"
              name="reference"
              value={formData.reference}
              handleChange={handleChange}
              error={Boolean(errors.reference)}
              helperText={errors.reference}
              width="100%"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <FTextField
              label="IMD"
              placeholder="Enter IMD code / name"
              id="imd"
              name="imd"
              value={formData.imd}
              handleChange={handleChange}
              error={Boolean(errors.imd)}
              helperText={errors.imd}
              width="100%"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <ConfigDropdown
              label="Month"
              name="month"
              id="month"
              value={formData.month}
              handleChange={(name, val) => handleDropdownChange(name, val)}
              menu={MONTH_MENU}
              error={Boolean(errors.month)}
              helperText={errors.month}
            />
          </Grid>

          {/* Policy & Product Information */}
          <Grid item xs={12} sx={{ mt: 1 }}>
            <MDTypography
              variant="subtitle2"
              sx={{
                fontSize: pxToRem(14),
                fontWeight: 600,
                color: "#344054",
                borderBottom: "1px solid #EAECF0",
                pb: 0.5,
              }}
            >
              Policy & Product Details
            </MDTypography>
          </Grid>

          <Grid item xs={12} sm={6}>
            <ConfigDropdown
              label="Product*"
              name="product"
              id="product"
              value={formData.product}
              handleChange={(name, val) => handleDropdownChange(name, val)}
              menu={PRODUCT_MENU}
              error={Boolean(errors.product)}
              helperText={errors.product}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <FTextField
              label="Insurance Company"
              placeholder="Enter insurance company name"
              id="insuranceCompany"
              name="insuranceCompany"
              value={formData.insuranceCompany}
              handleChange={handleChange}
              error={Boolean(errors.insuranceCompany)}
              helperText={errors.insuranceCompany}
              width="100%"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <FTextField
              label="Name of Plan"
              placeholder="Enter plan name (e.g. Comprehensive)"
              id="planName"
              name="planName"
              value={formData.planName}
              handleChange={handleChange}
              error={Boolean(errors.planName)}
              helperText={errors.planName}
              width="100%"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <FTextField
              label="Policy Number"
              placeholder="Enter policy number"
              id="policyNumber"
              name="policyNumber"
              value={formData.policyNumber}
              handleChange={handleChange}
              error={Boolean(errors.policyNumber)}
              helperText={errors.policyNumber}
              width="100%"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <ConfigDropdown
              label="Code"
              name="code"
              id="code"
              value={formData.code}
              handleChange={(name, val) => handleDropdownChange(name, val)}
              menu={CODE_MENU}
              error={Boolean(errors.code)}
              helperText={errors.code}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <ConfigDropdown
              label="Policy Status"
              name="policyStatus"
              id="policyStatus"
              value={formData.policyStatus}
              handleChange={(name, val) => handleDropdownChange(name, val)}
              menu={STATUS_MENU}
              error={Boolean(errors.policyStatus)}
              helperText={errors.policyStatus}
            />
          </Grid>

          {/* Dates & Financials */}
          <Grid item xs={12} sx={{ mt: 1 }}>
            <MDTypography
              variant="subtitle2"
              sx={{
                fontSize: pxToRem(14),
                fontWeight: 600,
                color: "#344054",
                borderBottom: "1px solid #EAECF0",
                pb: 0.5,
              }}
            >
              Dates & Premium
            </MDTypography>
          </Grid>

          <Grid item xs={12} sm={4}>
            <FTextField
              label="Inquiry Date"
              type="date"
              id="inquiryDate"
              name="inquiryDate"
              value={formData.inquiryDate}
              handleChange={handleChange}
              error={Boolean(errors.inquiryDate)}
              helperText={errors.inquiryDate}
              width="100%"
              inputProps={{
                max: moment().format("YYYY-MM-DD"),
              }}
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <FTextField
              label="Policy Issue Date"
              type="date"
              id="policyIssueDate"
              name="policyIssueDate"
              value={formData.policyIssueDate}
              handleChange={handleChange}
              error={Boolean(errors.policyIssueDate)}
              helperText={errors.policyIssueDate}
              width="100%"
              inputProps={{
                max: moment().format("YYYY-MM-DD"),
              }}
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <FTextField
              label="Renewal Date"
              type="date"
              id="renewalDate"
              name="renewalDate"
              value={formData.renewalDate}
              handleChange={handleChange}
              error={Boolean(errors.renewalDate)}
              helperText={errors.renewalDate}
              width="100%"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <FTextField
              label="Gross Premium"
              placeholder="Enter gross premium"
              id="grossPremium"
              name="grossPremium"
              value={formData.grossPremium}
              handleChange={handleChange}
              error={Boolean(errors.grossPremium)}
              helperText={errors.grossPremium}
              width="100%"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <FTextField
              label="Net Premium"
              placeholder="Enter net premium"
              id="netPremium"
              name="netPremium"
              value={formData.netPremium}
              handleChange={handleChange}
              error={Boolean(errors.netPremium)}
              helperText={errors.netPremium}
              width="100%"
            />
          </Grid>

          {/* Attachments Section - Matches Lead Bay Form UI */}
          <Grid item xs={12} sx={{ mt: 1 }}>
            <MDTypography
              variant="subtitle2"
              sx={{
                fontSize: pxToRem(14),
                fontWeight: 600,
                color: "#344054",
                borderBottom: "1px solid #EAECF0",
                pb: 0.5,
              }}
            >
              Policy Documents & Attachments
            </MDTypography>
          </Grid>

          <Grid item xs={12}>
            <MDBox sx={{ mt: pxToRem(8), mb: pxToRem(10), width: "100%" }}>
              <MDBox
                display="flex"
                flexDirection="row"
                justifyContent="flex-start"
                alignItems="center"
                gap={1.5}
                flexWrap="wrap"
                sx={{ mt: pxToRem(6) }}
              >
                {(formData.attachments || []).map((img, i) => (
                  <MDBox
                    display="flex"
                    borderRadius="8px"
                    position="relative"
                    key={img.url ? img.url : `attachment-${img.name}`}
                    sx={{
                      "&:hover .overlay": { display: "flex", borderRadius: "8px" },
                    }}
                  >
                    <img
                      src={
                        img?.name?.toLowerCase().includes(".pdf") ||
                        img?.url?.toLowerCase().includes(".pdf")
                          ? Pdf
                          : img?.url
                      }
                      alt="Preview"
                      width={70}
                      height={70}
                      style={{ borderRadius: "8px", objectFit: "cover" }}
                    />
                    <Box
                      display="none"
                      position="absolute"
                      top={0}
                      right={35}
                      bottom={0}
                      left={0}
                      alignItems="center"
                      justifyContent="center"
                      bgcolor="rgba(0, 0, 0, 0.5)"
                      className="overlay"
                    >
                      <Icon
                        sx={{
                          color: "white",
                          width: 24,
                          height: 24,
                          cursor: "pointer",
                        }}
                        onClick={() => handleImageFullView(img?.url, i)}
                      >
                        {Icons.VIEW2}
                      </Icon>
                    </Box>
                    <Box
                      display="none"
                      position="absolute"
                      top={0}
                      right={0}
                      bottom={0}
                      left={35}
                      alignItems="center"
                      justifyContent="center"
                      bgcolor="rgba(0, 0, 0, 0.5)"
                      className="overlay"
                    >
                      <Icon
                        sx={{
                          color: "white",
                          width: 24,
                          height: 24,
                          cursor: "pointer",
                        }}
                        onClick={() => handleRemoveAttachment(i)}
                      >
                        {Icons.CROSS2}
                      </Icon>
                    </Box>
                  </MDBox>
                ))}
                <MDBox
                  component="label"
                  width={70}
                  height={70}
                  borderRadius="8px"
                  sx={{
                    border: "2px dashed #D0D5DD",
                    borderRadius: "8px",
                    cursor: "pointer",
                    justifyContent: "center",
                    alignItems: "center",
                    display: "flex",
                  }}
                >
                  {uploading ? <CircularProgress size={24} color="info" /> : Icons.ADD}
                  <input
                    ref={fileInputRef}
                    type="file"
                    hidden
                    multiple
                    accept="image/jpeg,image/jpg,image/png,application/pdf"
                    onChange={handleFileUpload}
                  />
                </MDBox>
              </MDBox>
              <MDTypography variant="caption" sx={{ color: "#667085", mt: 1, display: "block" }}>
                Accepted: JPG, JPEG, PNG, PDF (click + to upload, hover on thumbnail to view or
                delete)
              </MDTypography>
            </MDBox>
          </Grid>
        </Grid>
      </BasicModal>

      {/* Full screen image preview modal matching Lead Bay */}
      {fullScreenImage && (
        <FullScreenImageComponent
          fullScreenImage={fullScreenImage}
          handleCloseFullView={() => setFullScreenImage(null)}
          handleNextImage={handleNextImage}
          handlePreviousImage={handlePreviousImage}
          image={formData.attachments}
          src={fullScreenImage}
        />
      )}
    </>
  );
}
